// PAGE: ComparePage.tsx – final polish: better spacing for desktop and mobile
// ------------------------------------------------------------------------------------------------- 
// • Desktop: tighter toolbar flex, slightly larger pill, sticky table header shadow
// • Mobile: better flex stacking, hide pill if too small

import { useMemo, useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { loadAllPropFirmData } from "../data/propFirmData";
import { ComparisonTableCompare } from "../components/ComparisonTableCompare";
import { getBestDiscount } from "../utils/getBestDiscount";
import { TableSkeleton } from "../components/LoadingSkeleton";

export function ComparePage() {
  const [accountSize, setAccountSize] = useState<number>(100000);
  const [firmData, setFirmData] = useState<Record<string, any>>({});
  const [activeGroup, setActiveGroup] = useState<string>("1-Step");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const data = await loadAllPropFirmData();
        setFirmData(data);
      } catch (error) {
        console.error('Error loading firm data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const filteredFirms = useMemo(() => {
    const list: any[] = [];
    Object.values(firmData).forEach((firm) => {
      firm.pricing?.forEach((price: any, idx: number) => {
        if (Number(price.accountSize) !== accountSize) return;
        const promo = getBestDiscount(firm.discounts || [], price);
        list.push({
          id: `${firm.id}-${price.accountSize}-${idx}`,
          slug: firm.id,
          name: firm.name,
          logo: firm.logo,
          pricing: [price],
          affiliateLink: firm.affiliateLink,
          features: firm.features,
          promo,
        });
      });
    });
    return list;
  }, [firmData, accountSize]);

  const canonicalUrl = 'https://www.propviper.com/compare';

  return (
    <>
      <Helmet>
        <title>Prop Firm Price Comparison – Find the Best Deal | PropViper</title>
        <meta name="description" content="Compare fees, profit splits, discounts and rules across top prop trading firms." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content="Prop Firm Price Comparison – Find the Best Deal | PropViper" />
        <meta property="og:description" content="Compare fees, profit splits, discounts and rules across top prop trading firms." />
        <meta property="og:image" content="https://propviper.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content="Prop Firm Price Comparison – Find the Best Deal | PropViper" />
        <meta name="twitter:description" content="Compare fees, profit splits, discounts and rules across top prop trading firms." />
        <meta name="twitter:image" content="https://propviper.com/twitter-image.jpg" />
      </Helmet>

      {/* HERO */}
      <section className="bg-gradient-to-br from-black to-gray-900 py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 text-yellow-400">Prop Firm Price Comparison – Find the Best Deal</h1>
          <p className="text-base sm:text-lg text-yellow-200 mb-6">
            Side-by-side breakdown of challenge fees, discounts, payout rules, and more.
          </p>
        </div>
      </section>

      {/* TOOLBAR */}
      <div className="sticky top-[64px] z-30 bg-gray-950/90 backdrop-blur-sm border-b border-yellow-900 shadow-sm shadow-yellow-900/30 mt-2">
        <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-yellow-200">Account Size:</label>
            <select
              className="bg-gray-800 text-yellow-200 border border-yellow-700 rounded px-3 py-2 text-sm w-[140px] sm:w-[160px] hover:border-yellow-500 focus:ring-2 focus:ring-yellow-400"
              value={accountSize}
              onChange={(e) => setAccountSize(Number(e.target.value))}
            >
              {[5000, 10000, 25000, 50000, 100000, 200000].map((s) => (
                <option key={s} value={s}>
                  ${s.toLocaleString()}
                </option>
              ))}
            </select>
            <span className="hidden sm:inline-block bg-yellow-800/30 text-yellow-200 px-3 py-1 rounded-full font-mono text-sm">
              ${accountSize.toLocaleString()}
            </span>
          </div>
          <div className="text-sm text-yellow-400 font-semibold whitespace-nowrap">
            Showing: <span className="text-yellow-300">{activeGroup}</span>
          </div>
        </div>
      </div>

      {/* DESKTOP sticky column header */}
      <div className="hidden sm:block sticky top-[110px] z-20 bg-gray-900 pt-2 shadow-md shadow-black/30">
        <div className="max-w-6xl mx-auto px-4 overflow-x-auto bg-gray-900">
          <table className="min-w-full table-fixed text-yellow-300 text-xs sm:text-sm font-semibold">
            <thead className="bg-gray-900">
              <tr className="border-b border-yellow-900">
                <th className="text-left px-2 py-3 sm:px-3 w-[24%]">Firm</th>
                <th className="text-left px-2 py-3 sm:px-3 w-[14%]">Max DD</th>
                <th className="text-left px-2 py-3 sm:px-3 w-[14%]">Daily DD</th>
                <th className="text-left px-2 py-3 sm:px-3 w-[14%]">Leverage</th>
                <th className="text-left px-2 py-3 sm:px-3 w-[12%]">Profit Split</th>
                <th className="text-left px-2 py-3 sm:px-3 w-[12%]">Fee</th>
                <th className="text-left px-2 py-3 sm:px-3 w-[20%]">Promo</th>
              </tr>
            </thead>
          </table>
        </div>
      </div>

      {/* TABLE SECTION */}
      <section className="bg-gray-950 text-yellow-100 py-16">
        <div className="max-w-6xl mx-auto px-4 mt-6">
          <p className="text-center text-yellow-500 mt-2 text-sm sm:hidden">Scroll sideways to view all details →</p>
          {isLoading ? (
            <div className="max-w-6xl mx-auto">
              <TableSkeleton rows={5} />
            </div>
          ) : filteredFirms.length === 0 ? (
            <div className="text-center text-yellow-300 py-16">No matching firms found.</div>
          ) : (
            <ComparisonTableCompare
              firms={filteredFirms}
              accountSize={accountSize}
              onGroupChange={setActiveGroup}
            />
          )}
        </div>
      </section>
    </>
  );
}

export default ComparePage;
